import type { InvestorFirmAdminRead as CrmInvestorFirm } from '@capital-markets-gateway/api-client-rolodex';
import { Box, Card, CardContent, CardHeader, Stack, Typography } from '@cmg/design-system';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import React from 'react';

import { LinkedStatus } from '../../../../../types/domain/entity-matcher/constants';
import type { FirmMatchN1MappingData } from '../../../../../types/domain/firm-match/FirmMatchN1MappingData';
import FirmLinkedStatusChange from './FirmLinkedStatusChange';
import { MappingHeader } from './MappingHeader';

type LinkOrganizationMappingComparisonProps = {
  crmInvestorFirm: CrmInvestorFirm;
  existingCmgFirmName: string | null;
  linkTargetDisplayName: string | null;
  isInvestorFirmLinked: boolean;
  otherCrmFirmsLinkedToTarget: FirmMatchN1MappingData[];
};

export const LinkOrganizationMappingComparison: React.FC<
  LinkOrganizationMappingComparisonProps
> = ({
  crmInvestorFirm,
  existingCmgFirmName,
  linkTargetDisplayName,
  isInvestorFirmLinked,
  otherCrmFirmsLinkedToTarget,
}) => {
  return (
    <Stack direction="row" spacing={2} alignItems="stretch">
      <Box flex={1} display="flex">
        <Card variant="outlined">
          <CardHeader title={<Typography variant="subtitle1">Existing Mapping</Typography>} />
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <MappingHeader />

            <FirmLinkedStatusChange
              from={crmInvestorFirm.name}
              to={existingCmgFirmName}
              pendingLinkedStatus={LinkedStatus.LINKED}
              crmState="neutral"
              cmgState="neutral"
            />

            {linkTargetDisplayName &&
              otherCrmFirmsLinkedToTarget.map(firm => (
                <FirmLinkedStatusChange
                  key={`existing-${firm.id}`}
                  from={firm.name}
                  to={linkTargetDisplayName}
                  pendingLinkedStatus={LinkedStatus.LINKED}
                  crmState="neutral"
                  cmgState="neutral"
                />
              ))}
          </CardContent>
        </Card>
      </Box>

      <Box display="flex" alignItems="center" justifyContent="center">
        <ArrowForwardIcon color="disabled" />
      </Box>

      <Box flex={1} display="flex">
        <Card variant="outlined">
          <CardHeader title={<Typography variant="subtitle1">New Mapping</Typography>} />
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <MappingHeader />

            {isInvestorFirmLinked && existingCmgFirmName && (
              <FirmLinkedStatusChange
                from={crmInvestorFirm.name}
                to={existingCmgFirmName}
                pendingLinkedStatus={LinkedStatus.NOT_LINKED}
                crmState="removed"
                cmgState="removed"
              />
            )}

            <FirmLinkedStatusChange
              from={crmInvestorFirm.name}
              to={linkTargetDisplayName}
              pendingLinkedStatus={LinkedStatus.LINKED}
              crmState="added"
              cmgState="added"
            />

            {linkTargetDisplayName &&
              otherCrmFirmsLinkedToTarget.map(firm => (
                <FirmLinkedStatusChange
                  key={`new-${firm.id}`}
                  from={firm.name}
                  to={linkTargetDisplayName}
                  pendingLinkedStatus={LinkedStatus.LINKED}
                  crmState="neutral"
                  cmgState="neutral"
                />
              ))}
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
};
